/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Table cells config for labels import
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.10.0
 * @since 1.3.0
 */

import range from 'lodash/range';

import { icons } from '../../../../assets';
import { formatNumberToPhone } from '../../../../components/common/PhoneMask/formatNumberToPhone';
import { ITableField } from '../../../../components/Table/reducers/tableReducer';
import { EColumnCell } from '../../../../components/Table/types/columnTypes';
import { EColorStatus } from '../../../../modules/app/appConstants';
import {
    ILabelsImportTableTranslation,
    ILabelsImportTranslation,
} from '../../../../modules/internationalization/types/wizards/labelsImport';
import { stringFormat } from '../../../../utils/stringFormat';
import { ELabelsImportField, ELabelsImportRowStatus } from '../../labelsImportConstants';
import { ILabelsImportValidatedSubscriberResponse } from '../../labelsImportTypes';

export const labelsImportColumnsWidth: { [Key in ELabelsImportField]: number } = {
    [ELabelsImportField.LABEL]: 600,
    [ELabelsImportField.MSISDN]: 600,
    [ELabelsImportField.STATUS]: 600,
};

export const labelsImportColumnNames: ELabelsImportField[] = [
    ELabelsImportField.MSISDN,
    ELabelsImportField.STATUS,
    ELabelsImportField.LABEL,
];

export const getColumnFilterType = (name: string): string => {
    switch (name) {
        case ELabelsImportField.LABEL:
        case ELabelsImportField.MSISDN:
        case ELabelsImportField.STATUS:
        default:
            return '';
    }
};

export const getColumnCellType = (name: string): string => {
    switch (name) {
        case ELabelsImportField.STATUS:
            return EColumnCell.statusLabelsImport;
        case ELabelsImportField.LABEL:
        case ELabelsImportField.MSISDN:
        default:
            return EColumnCell.labelsImportConflict;
    }
};

export const getColumnFixedType = (name: string): string | boolean => {
    switch (name) {
        case ELabelsImportField.MSISDN:
        case ELabelsImportField.STATUS:
            return 'left';
        default:
            return false;
    }
};

type TFormLabelsImportTableField =
    (props: Partial<Pick<ITableField<ELabelsImportField>, 'name' | 'label' | 'order'>>) =>
        ITableField<ELabelsImportField>

export const formLabelsImportTableField: TFormLabelsImportTableField = (props) => ({
    name: props.name ?? ELabelsImportField.MSISDN,
    label: props.label ?? '',
    checked: true,
    order: props.order ?? 0,
    resizable: true,
    size: 200,
    type: 'S',
})

export const formLabelsImportTableFields =
    (labelColumnsCount: number, translation: ILabelsImportTableTranslation): Array<ITableField<ELabelsImportField>> => {
        const msisdnField = formLabelsImportTableField({
            name: ELabelsImportField.MSISDN,
            order: 0,
            label: translation[ELabelsImportField.MSISDN],
        });

        const statusField = formLabelsImportTableField({
            name: ELabelsImportField.STATUS,
            order: 1,
            label: translation[ELabelsImportField.STATUS],
        });

        const labelsFields = range(labelColumnsCount).map(number => {
            return formLabelsImportTableField({
                name: `${ELabelsImportField.LABEL}${+number + 1}` as ELabelsImportField,
                order: number + 2,
                label: stringFormat(translation[ELabelsImportField.LABEL], +number + 1),
            })
        });

        return [{ ...msisdnField }, { ...statusField }, ...labelsFields];
    }

export const formLabelsImportTableList =
    (tableData: ILabelsImportValidatedSubscriberResponse[], translation: ILabelsImportTranslation) => {
        const { codes: errorCodes, defaultMessage } = translation.error;

        return tableData.map(row => {
            const subscriberConflict = row.subscriber.conflict ?
                errorCodes[row.subscriber.conflict] ?? defaultMessage : '';

            const msisdn = {
                name: formatNumberToPhone(row.subscriber.msisdn),
                conflict: subscriberConflict,
            }

            const status = {
                id: row.status,
                def: translation.status[row.status],
            }

            const labels = row.labels.reduce((accum, label, index) => {
                const labelConflict = label.conflict ? errorCodes[label.conflict] ?? defaultMessage : '';
                return {
                    ...accum,
                    [`label${index + 1}`]: {
                        name: label.name,
                        conflict: labelConflict,
                    },
                }
            }, {})
            return {
                msisdn,
                status,
                ...labels,
            }
        })
    };

type TGetLabelsImportStatusIcon = (rowStatus: ELabelsImportRowStatus) =>
    {
        icon: string | undefined;
        colorState: EColorStatus | undefined;
    }

export const getLabelsImportStatusIcon: TGetLabelsImportStatusIcon = (rowStatus) => {
    switch (rowStatus) {
        case ELabelsImportRowStatus.NOT_IMPORTED:
            return {
                colorState: EColorStatus.ERROR,
                icon: icons.cancelErrorSmallIcon,
            }
        case ELabelsImportRowStatus.FULLY_IMPORTED:
            return {
                colorState: EColorStatus.SUCCESS,
                icon: icons.checkedSuccessSmallIcon,
            }
        case ELabelsImportRowStatus.PARTIALLY_IMPORTED:
            return {
                colorState: EColorStatus.WARNING,
                icon: icons.halfSmallIcon,
            }
        default:
            return {
                icon: undefined,
                colorState: undefined,
            };
    }
};
